import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCheck, LuCopy, LuInfo, LuLoaderCircle, LuMail, LuSparkles, LuTriangleAlert, LuUserCog, LuX } from 'react-icons/lu';
import { auth, db } from '../../config/firebaseConfig';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import { getInitials } from '../../components/ui/Avatar';
import { GLASS_CHIP } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import { Field, SelectInput, TextInput } from './employeeForm/FormControls';

// Stored role values (Users.role, Employees.authorization) and their text keys
const ROLE_OPTIONS = [
  { value: 'employee', key: 'employee' },
  { value: 'admin', key: 'admin' },
  { value: 'Finans Yöneticisi', key: 'finance' },
  { value: 'Proje Yöneticisi', key: 'project' },
  { value: 'Saha Koordinatörü', key: 'fieldCoordinator' },
  { value: 'İnsan Kaynakları', key: 'hr' },
  { value: 'SMO Lead', key: 'smoLead' },
];

const generateStrongPassword = (length = 12) => {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let generatedPassword = "";
  for (let i = 0, n = charset.length; i < length; ++i) {
    generatedPassword += charset.charAt(Math.floor(Math.random() * n));
  }
  // Ensure password meets common complexity requirements (optional)
  if (!/[A-Z]/.test(generatedPassword)) generatedPassword += 'A';
  if (!/[a-z]/.test(generatedPassword)) generatedPassword += 'a';
  if (!/[0-9]/.test(generatedPassword)) generatedPassword += '1';
  if (!/[!@#$%^&*()_+]/.test(generatedPassword)) generatedPassword += '!';

  // Trim to desired length if extra characters were added
  return generatedPassword.slice(0, length);
};

/** "Hesap Oluştur": portal account (Firebase Auth + Users) for an employee. */
function CreateUserModal({ employee, onClose }) {
  const { t, language } = useTranslation();
  const formId = useId();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('employee'); // Default role
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fullName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword();
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    setError(''); // Clear error when generating
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success(t('employees.modals.account.copied'));
    } catch (copyError) {
      toast.error(t('employees.modals.account.copyFailed'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password !== confirmPassword) {
      setError(t('employees.modals.account.errors.mismatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('employees.modals.account.errors.short'));
      return;
    }

    setIsLoading(true);

    // Define a unique name for the secondary app
    const secondaryAppName = `userCreationApp_${Date.now()}`;
    let secondaryApp = null; // Keep track of the secondary app instance

    try {
      // Get config from the default app instance
      const primaryAppConfig = auth.app.options;
      if (!primaryAppConfig) {
        throw new Error("Could not retrieve Firebase config from default app.");
      }

      // 1. Initialize secondary Firebase app using the retrieved config
      secondaryApp = initializeApp(primaryAppConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      // 2. Create Firebase Auth user using the secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, employee.eMail, password);
      const user = userCredential.user;

      // 3. Create document in 'Users' collection (using the primary db instance)
      await setDoc(doc(db, 'Users', user.uid), {
        email: user.email,
        role: role,
        employeeId: employee.id,
        createdAt: serverTimestamp(),
        displayName: `${employee.eName} ${employee.eSurname}`
      });

      // 4. Update the employee's authorization field in Employees collection
      await setDoc(doc(db, 'Employees', employee.id), {
        authorization: [role] // Set the role as authorization
      }, { merge: true }); // Use merge to not overwrite other fields

      // 4. Send credentials email via Cloud Function (non-blocking for user creation)
      try {
        const response = await fetch(
          'https://us-central1-habcare-8ecad.cloudfunctions.net/sendAccountCredentialsEmailV2',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: employee.eMail,
              password: password,
              displayName: `${employee.eName} ${employee.eSurname}`,
              loginUrl: 'https://management.habcarecro.com',
              fromName: 'HabcareCRO Yönetim',
            }),
          }
        );
        if (!response.ok) {
          throw new Error('Email function responded with non-OK status');
        }
        toast.success(t('employees.modals.account.toast.createdAndMailed'));
      } catch (mailErr) {
        console.error('Error sending credentials email:', mailErr);
        toast.warn(t('employees.modals.account.toast.mailFailed'));
      }

      toast.success(t('employees.modals.account.toast.created', { email: user.email }));
      onClose(); // Close modal on success

    } catch (createError) {
      console.error('Error creating user:', createError);
      if (createError.code === 'auth/email-already-in-use') {
        setError(t('employees.modals.account.errors.emailInUse'));
        toast.error(t('employees.modals.account.toast.emailInUse'));
      } else if (createError.code === 'auth/invalid-email') {
        setError(t('employees.modals.account.errors.invalidEmail'));
        toast.error(t('employees.modals.account.toast.invalidEmail'));
      } else if (createError.code === 'auth/weak-password') {
        setError(t('employees.modals.account.errors.weak'));
        toast.error(t('employees.modals.account.toast.weak'));
      } else {
        setError(t('employees.modals.account.errors.generic', { message: createError.message }));
        toast.error(t('employees.modals.account.toast.generic'));
      }
    } finally {
      setIsLoading(false);
      // 6. Clean up: Delete the secondary app instance regardless of success/failure
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
          console.log(`Secondary app ${secondaryAppName} deleted.`);
        } catch (deleteError) {
          console.error(`Error deleting secondary app ${secondaryAppName}:`, deleteError);
        }
      }
    }
  };

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isLoading) onClose();
      }}
      size="md"
      title={t('employees.modals.account.title')}
      subtitle={t('employees.modals.account.subtitle')}
      icon={(
        <GlassIcon gradient="tw-from-amber-300 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuUserCog aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose()} disabled={isLoading}>{t('employees.form.cancel')}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={isLoading}>
            {isLoading
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuUserCog aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isLoading ? t('employees.modals.account.creating') : t('employees.modals.account.submit')}
          </Button>
        </>
      )}
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="tw-space-y-4">
        <div className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-3 ${GLASS_CHIP}`}>
          <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="avatarLg">
            {getInitials(employee.eName, employee.eSurname)}
          </GlassIcon>
          <div className="tw-min-w-0">
            <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{fullName || '-'}</p>
            <p className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
              <LuMail aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              <span className="tw-truncate">{employee.eMail || '—'}</span>
            </p>
          </div>
        </div>

        {!employee.eMail && (
          <p className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-bg-amber-400/10 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-snug tw-text-amber-800 dark:tw-text-amber-200">
            <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            <span className="tw-min-w-0">{t('employees.modals.account.noEmail')}</span>
          </p>
        )}

        <Field label={t('employees.modals.account.password')} htmlFor={`${formId}-password`} required>
          <div className="tw-flex tw-gap-2">
            {/* Plain text: the generated password has to be readable */}
            <TextInput
              id={`${formId}-password`}
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="tw-min-w-0 tw-font-mono"
            />
            <Button onClick={handleGeneratePassword} title={t('employees.modals.account.generateHint')} className="tw-shrink-0">
              <LuSparkles aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-500" />
              {t('employees.modals.account.generate')}
            </Button>
            {password && (
              <IconButton label={t('employees.modals.account.copy')} onClick={handleCopyPassword}>
                <LuCopy aria-hidden="true" className="tw-h-4 tw-w-4" />
              </IconButton>
            )}
          </div>
        </Field>

        <Field
          label={t('employees.modals.account.confirmPassword')}
          htmlFor={`${formId}-confirm`}
          required
          hint={confirmPassword ? (
            <span className={`tw-inline-flex tw-items-center tw-gap-1 ${passwordsMatch ? 'tw-text-emerald-600 dark:tw-text-emerald-400' : 'tw-text-rose-600 dark:tw-text-rose-400'}`}>
              {passwordsMatch ? <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" /> : <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
              {passwordsMatch ? t('employees.modals.account.match') : t('employees.modals.account.noMatch')}
            </span>
          ) : undefined}
        >
          <TextInput
            id={`${formId}-confirm`}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>

        <Field label={t('employees.modals.account.role')} htmlFor={`${formId}-role`}>
          <SelectInput
            id={`${formId}-role`}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={ROLE_OPTIONS.map((option) => ({ value: option.value, label: t(`employees.modals.account.roles.${option.key}`) }))}
          />
        </Field>

        {error && (
          <p role="alert" className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-3 tw-text-sm tw-font-medium tw-leading-snug tw-text-rose-700 dark:tw-text-rose-300">
            <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            <span className="tw-min-w-0">{error}</span>
          </p>
        )}

        <p className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
          <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
          {t('employees.modals.account.mailNote')}
        </p>
      </form>
    </Modal>
  );
}

CreateUserModal.propTypes = {
  employee: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateUserModal;
